// The Jobs area's vocabulary and arithmetic, pure (no React) so
// jobModel.selftest.ts runs it on its own: a job's status as a word (the
// run's word once it was cancelled or failed; "Done – 1 check failed"), what
// happens next in the operator's words, the clock line, step kinds, what a
// check or the plan came to, planned-against-used minutes, tokens per step,
// revision kinds, what lattice did with each revision (steps and their copies
// by title), fault severities, who reads a note and the notes as sent, and
// the refusals of the job routes (and the plan check's) as plain sentences.
//
// Vocabulary: job, brief, plan, step, checkpoint, check, deliverable,
// deadline, reserve, revision, send-back, Lead.

import type { Fault, FaultSeverity, JobCheckRow, JobClock, JobNoteReader, JobStatus, JobStepRow, NodeUsage, Revision, RevisionKind, TeamMember } from "@/api/jobs";
import { workflowError, workflowErrorCode } from "@/api/workflows";
import type { StatusTone } from "@/components/ui/StatusBadge";
import { dayKey, formatDateTime, formatDuration, formatTime, joinList, pluralize, toDate } from "@/lib/format";

import { STATUS, isFailedNode, isLiveRun, makeStatusWord, runStatusWord, statusWord, type StatusWord } from "../status";
import { localTimes } from "../time";
import { TEAM_MISSING_TEXT } from "./teamModel";

export const JOBS_OFF = "Switch Team tasks on in Settings → General";

/** A job's own states, in the Workflows area's one vocabulary (../status.ts). */
export const JOB_STATUS: Record<JobStatus, StatusWord> = {
  planning: STATUS.planning,
  awaiting_approval: STATUS.awaiting_approval,
  running: STATUS.running,
  finishing: STATUS.finishing,
  done: STATUS.done,
};

/**
 * The job's own word, unless its run says otherwise (cancelled, failed,
 * paused, done with failures). A plan waiting for approval pauses the run,
 * but the job's word is the one to act on; a job that finished with checks
 * that failed says so ("Done – 1 check failed"). The Runs list says the
 * same of the job's run (../status.ts runStatusWord).
 */
export function jobStatusWord(jobStatus: string | null | undefined, runStatus: string | null | undefined, failedChecks = 0): StatusWord {
  return runStatusWord(runStatus, { status: jobStatus || null, failed_checks: failedChecks });
}

/** The plan waits for approval: the run is paused for it, and only the job's Approve starts it. */
export function awaitingApproval(jobStatus: string | null | undefined, runStatus: string | null | undefined): boolean {
  return jobStatus === "awaiting_approval" && isLiveRun(runStatus);
}

export const isLiveJob = isLiveRun;

// ─── the clock ───

/** "14:32" today, else "Tomorrow 09:00" / "Wed 30 Sep, 09:00". "" for nothing. */
export function atLabel(iso: string | null | undefined, now: Date): string {
  const d = toDate(iso ?? null);
  if (!d) return "";
  return dayKey(d) === dayKey(now) ? formatTime(d) : formatDateTime(d, {}, now);
}

/** "27 min left", "1 h 5 min left"; "no time left" at zero. */
export function minutesLeftLabel(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return "";
  return minutes <= 0 ? "no time left" : `${formatDuration(minutes * 60)} left`;
}

/** The header's clock: "Deadline 14:32 · 27 min left · reserve from 14:17" (live), "Deadline was 14:32" (ended). */
export function clockParts(clock: JobClock, live: boolean, now: Date): string[] {
  const deadline = atLabel(clock.deadline_at, now);
  if (!deadline) return [];
  if (!live) return [`Deadline was ${deadline}`];
  const reserve = atLabel(clock.reserve_at, now);
  const inReserve = clock.minutes_to_reserve <= 0 && clock.reserve_minutes > 0;
  return [
    `Deadline ${deadline}`,
    minutesLeftLabel(clock.minutes_left),
    reserve && clock.reserve_minutes > 0 ? (inReserve ? `in the reserve since ${reserve}` : `reserve from ${reserve}`) : "",
  ].filter(Boolean);
}

// ─── steps ───

export type StepKind = "work" | "plan" | "checkpoint" | "check" | "handover";

export const STEP_KIND: Record<StepKind, string> = {
  work: "Step",
  plan: "Plan",
  checkpoint: "Checkpoint",
  check: "Check",
  handover: "Handover",
};

export function stepKind(kind: string | null | undefined): StepKind {
  return kind && kind in STEP_KIND ? (kind as StepKind) : "work";
}

/** What an ended check or plan came to (JobStepRow / JobCheckRow `outcome`), said instead of its run state. */
export const OUTCOME: Record<string, StatusWord> = {
  passed: makeStatusWord("Passed", "success", "done"),
  failed: makeStatusWord("Failed", "danger", "failed"),
  not_recorded: makeStatusWord("Failed", "danger", "failed"),
  not_checked: makeStatusWord("Not checked", "warning", "warning"),
  refused: makeStatusWord("Refused", "danger", "failed"),
};

/**
 * A stage's status as the run view says it; a fan-out where some branches
 * failed is the run's word. A check says what it found and a plan that was
 * refused says so, never a green "Done" (the run state only says the turn ended).
 */
export function stepStatusWord(status: string | null | undefined, outcome?: string | null): StatusWord {
  if (outcome && OUTCOME[outcome]) return OUTCOME[outcome];
  return statusWord(status);
}

/** A check's badge: "Passed", "Passed after 1 send-back", "Failed", "Failed – faults not recorded", "Not checked"; its run state while it is to come. */
export function checkStatusWord(check: Pick<JobCheckRow, "status" | "outcome" | "send_backs">): StatusWord {
  if (check.outcome === "not_recorded") return makeStatusWord("Failed – faults not recorded", "danger", "failed");
  if (check.outcome === "passed" && (check.send_backs ?? 0) > 0) return makeStatusWord(`Passed after ${pluralize(check.send_backs, "send-back")}`, "success", "done");
  return stepStatusWord(check.status, check.outcome);
}

/** Checks that ended failed (faults open, or a FAIL with none recorded). */
export function failedCheckCount(checks: readonly Pick<JobCheckRow, "outcome">[] | null | undefined): number {
  return (checks ?? []).filter((c) => c.outcome === "failed" || c.outcome === "not_recorded").length;
}

/** Steps in plan order with the handover last (the engine appends it; this only makes sure). */
export function orderedSteps(steps: readonly JobStepRow[]): JobStepRow[] {
  return [...steps.filter((s) => stepKind(s.kind) !== "handover"), ...steps.filter((s) => stepKind(s.kind) === "handover")];
}

/**
 * "12 / 30 min", "<1 / 5 min", "– / 30 min" before it ran: whole minutes,
 * used measured from the step's start to its end (the run view's span, so
 * a step that overran its limit says so).
 */
export function minutesLabel(used: number | null | undefined, planned: number): string {
  const u = used == null ? "–" : used > 0 && used < 0.5 ? "<1" : String(Math.round(used));
  return `${u} / ${planned} min`;
}

/** The used share of the planned minutes for the bar (0..1) and whether it ran over (in the whole minutes the label shows). */
export function minutesShare(used: number | null | undefined, planned: number): { share: number; over: boolean } {
  if (used == null || !(planned > 0)) return { share: 0, over: false };
  return { share: Math.max(0, Math.min(1, used / planned)), over: Math.round(used) > planned };
}

/**
 * The minutes cell's colour: a step that failed or ran out of time takes
 * its status's (red, amber), one that ran past its planned minutes amber;
 * null when it kept to its plan (the run view's colours for the same step).
 */
export function minutesTone(step: Pick<JobStepRow, "status" | "used_minutes" | "planned_minutes">): StatusTone | null {
  if (isFailedNode(step.status)) return statusWord(step.status).tone;
  return minutesShare(step.used_minutes, step.planned_minutes).over ? "warning" : null;
}

export const DEPTH_WORD: Record<string, string> = { quick: "Quick", standard: "Standard", deep: "Deep" };

/** Who does a step: the subagent's name, or the role on the general worker. */
export function stepWho(step: Pick<JobStepRow, "subagent" | "role">, nameOf: (template: string) => string): string {
  if (step.role) return step.role;
  return step.subagent ? nameOf(step.subagent) : "";
}

/** A person inside a sentence ("asked by you", "approved by Julia"): the directory's "You" lower-cased. */
export function whoInline(template: string | null | undefined, nameOf: (template: string) => string): string {
  const name = nameOf(template ?? "");
  return name === "You" ? "you" : name;
}

// A run that has ended says so whatever the job's own word still is.
const ENDED_RUN = new Set(["completed", "completed_with_failures", "failed", "cancelled"]);

/**
 * What happens next, for the operator. The view's `next` is written for the
 * requesting agent (it quotes `job approve` commands), so the page says it
 * from the job's status instead; a job that is done (or whose run ended)
 * says how its run ended. A failed run's reason is the header's alert (in
 * the operator's time of day), so it is not said here a second time.
 */
export function nextLine(
  job: { status?: string | null } | null | undefined,
  run: { status?: string | null } | null | undefined,
  failedChecks = 0,
): string {
  const status = run?.status && ENDED_RUN.has(run.status) ? "done" : run?.status === "paused" && job?.status !== "awaiting_approval" ? "paused" : job?.status;
  switch (status) {
    case "paused":
      return "Paused.";
    case "planning":
      return "The Lead is planning.";
    case "awaiting_approval":
      return "The plan needs your approval.";
    case "running":
      return "Running.";
    case "finishing":
      return "Finishing: the checks and the handover pack.";
    case "done":
      if (run?.status === "failed") return "Failed.";
      if (run?.status === "cancelled") return "Cancelled.";
      if (run?.status === "completed_with_failures") return "Finished, with failures.";
      return failedChecks > 0 ? `Finished, but ${pluralize(failedChecks, "check")} failed: see the open faults.` : "Finished.";
    default:
      return "";
  }
}

/** A team member as "subagent name" or "role (general worker)". */
export function teamWho(member: TeamMember, nameOf: (template: string) => string): string {
  if (member.subagent) return nameOf(member.subagent);
  return member.role ?? "";
}

// ─── tokens (the Lattice model proxy's count per node) ───

const ZERO: NodeUsage = {
  input: 0, output: 0, cache_read: 0, cache_write: 0, calls: 0,
  advisor_calls: 0, advisor_input: 0, advisor_output: 0,
};

function addUsage(a: NodeUsage, b: NodeUsage): NodeUsage {
  return {
    input: a.input + (b.input ?? 0),
    output: a.output + (b.output ?? 0),
    cache_read: a.cache_read + (b.cache_read ?? 0),
    cache_write: a.cache_write + (b.cache_write ?? 0),
    calls: a.calls + (b.calls ?? 0),
    advisor_calls: (a.advisor_calls ?? 0) + (b.advisor_calls ?? 0),
    advisor_input: (a.advisor_input ?? 0) + (b.advisor_input ?? 0),
    advisor_output: (a.advisor_output ?? 0) + (b.advisor_output ?? 0),
  };
}

/** A step's usage: its stage's node (`<stage>`) and every branch of it (`<stage>[k]`); null when none. */
export function stepUsage(usage: Record<string, NodeUsage> | null | undefined, stageId: string): NodeUsage | null {
  const nodes = Object.entries(usage ?? {}).filter(([node]) => node === stageId || node.startsWith(`${stageId}[`));
  return nodes.length ? nodes.reduce((sum, [, u]) => addUsage(sum, u), ZERO) : null;
}

/** The whole job's usage; null when none was recorded. */
export function totalUsage(usage: Record<string, NodeUsage> | null | undefined): NodeUsage | null {
  const nodes = Object.values(usage ?? {});
  return nodes.length ? nodes.reduce(addUsage, ZERO) : null;
}

/** Tokens in and out (cache reads and writes are said apart, in the tooltip). */
export function tokenCount(u: NodeUsage): number {
  return u.input + u.output;
}

/** "950", "4.2k", "148k", "1.2M", "12M". */
export function formatTokens(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "";
  const short = (v: number, unit: string) => `${String(v < 10 ? Math.round(v * 10) / 10 : Math.round(v)).replace(/\.0$/, "")}${unit}`;
  if (n < 1000) return String(Math.round(n));
  // 999,600 rounds to "1000k": say "1M".
  if (n < 999_500) return short(n / 1000, "k");
  return short(n / 1_000_000, "M");
}

/** The cell's tooltip: "input 120k · output 28k · 3 calls" (+ cache reads and writes when there were any). */
export function tokensTitle(u: NodeUsage): string {
  return [
    `input ${formatTokens(u.input)}`,
    `output ${formatTokens(u.output)}`,
    u.cache_read ? `cache read ${formatTokens(u.cache_read)}` : "",
    u.cache_write ? `cache write ${formatTokens(u.cache_write)}` : "",
    `${u.calls} ${u.calls === 1 ? "call" : "calls"}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

// ─── revisions, faults ───

export const REVISION_KIND: Record<RevisionKind, string> = {
  plan: "Plan",
  decision: "Decision",
  faults: "Faults sent back",
  reserve: "Reserve began",
  stop: "Stopped",
  refused: "Refused",
};

export const REVISION_TONE: Record<RevisionKind, StatusTone> = {
  plan: "accent",
  decision: "info",
  faults: "warning",
  reserve: "warning",
  stop: "danger",
  refused: "danger",
};

/** The Lead's plans the plan check refused, oldest first (a checkpoint's refused decision is not one). */
export function refusedPlans(revisions: readonly Revision[] | null | undefined): Revision[] {
  return (revisions ?? []).filter((r) => r.kind === "refused" && r.by === "plan").sort((a, b) => a.n - b.n);
}

/**
 * A step by its title, or one copy of it: the node `test[2]` of a step that
 * runs in three copies is "Test (copy 2 of 3)". The id itself when it is no
 * step of the job.
 */
export function stepTitle(id: string, steps: readonly Pick<JobStepRow, "id" | "title" | "nodes">[]): string {
  const exact = steps.find((s) => s.id === id);
  if (exact) return exact.title || id;
  const copy = /^(.+)\[(\d+)\]$/.exec(id);
  if (!copy) return id;
  const step = steps.find((s) => s.id === copy[1]);
  return `${step?.title || copy[1]} (copy ${copy[2]}${step && step.nodes > 1 ? ` of ${step.nodes}` : ""})`;
}

/**
 * The Lead's decision stopped the steps and the reserve began: recorded on
 * the decision, or (a job from before that) the reserve revision after it
 * names its checkpoint as the cause.
 */
export function decisionStopped(r: Revision, revisions: readonly Revision[]): boolean {
  if (r.kind !== "decision") return false;
  if (r.stopped) return true;
  const cause = `the Lead stopped the steps at ${r.by}:`;
  return revisions.some((x) => x.kind === "reserve" && x.n > r.n && x.summary.startsWith(cause));
}

/** One line of what lattice did with a revision; one with no label is a sentence of its own. */
export interface RevisionLine {
  label: string;
  text: string;
  refused?: boolean;
}

/**
 * What lattice did with a revision, and what it did not, as lines. The
 * reserve's own revision says which steps were told to wrap up and which
 * were cancelled before they started (the Lead neither dropped nor redid
 * them); a decision, the steps it added, dropped (one that finished anyway
 * says so) and redid, and that it stopped the steps; a check, whom its
 * faults went back to; then every step not added and every refusal.
 */
export function revisionLines(r: Revision, steps: readonly JobStepRow[], revisions: readonly Revision[]): RevisionLine[] {
  const title = (id: string) => stepTitle(id, steps);
  const lines: RevisionLine[] = [];
  if (r.kind === "reserve") {
    if (r.redone?.length) lines.push({ label: "Told to wrap up", text: joinList(r.redone.map(title)) });
    if (r.dropped?.length) lines.push({ label: "Cancelled before starting", text: joinList(r.dropped.map(title)) });
  } else {
    // a plan's own revision adds every step: the plan card lists them
    if (r.kind !== "plan" && r.added?.length) lines.push({ label: "Added", text: joinList(r.added.map(title)) });
    if (r.dropped?.length) {
      const finished = (id: string) => steps.find((s) => s.id === id)?.status === "completed";
      lines.push({ label: "Dropped", text: joinList(r.dropped.map((id) => (finished(id) ? `${title(id)} (it finished before it could be stopped)` : title(id)))) });
    }
    if (r.redone?.length) lines.push({ label: r.kind === "faults" ? "Sent back to" : "Redone", text: joinList(r.redone.map(title)) });
    if (decisionStopped(r, revisions)) lines.push({ label: "", text: "Stopped the steps; the reserve began." });
  }
  if (r.not_added?.length) lines.push({ label: "Not added", text: joinList(r.not_added), refused: true });
  for (const reason of r.refused ?? []) lines.push({ label: r.kind === "refused" ? "Why" : "Not applied", text: plainRefusal(reason), refused: true });
  return lines;
}

export const SEVERITY: Record<FaultSeverity, StatusWord> = {
  blocker: makeStatusWord("Blocker", "danger", "failed"),
  major: makeStatusWord("Major", "warning", "warning"),
  minor: makeStatusWord("Minor", "neutral", "unknown"),
};

const SEVERITY_ORDER: Record<FaultSeverity, number> = { blocker: 0, major: 1, minor: 2 };

/** Blockers first, then major, then minor; otherwise as given. */
export function bySeverity(faults: readonly Fault[]): Fault[] {
  return faults.map((f, i) => ({ f, i })).sort((a, b) => (SEVERITY_ORDER[a.f.severity] ?? 3) - (SEVERITY_ORDER[b.f.severity] ?? 3) || a.i - b.i).map((x) => x.f);
}

// ─── notes ───

/** Who reads a note sent now, as a sentence (the note field's placeholder, and why it is off). */
export function noteReaderLine(reader: JobNoteReader | null | undefined): string {
  switch (reader?.kind) {
    case "checkpoint": {
      const at = reader.title ? reader.title.charAt(0).toLowerCase() + reader.title.slice(1) : "its next checkpoint";
      return `The Lead reads it at ${at}.`;
    }
    case "handover":
      return "No checkpoint is left: the writer of the handover pack reads it.";
    case "plan":
      return "The Lead reads it at its first checkpoint; if the plan has none, the writer of the handover pack does.";
    case "none":
      return "No checkpoint is left and the handover has started: nobody would read a note now.";
    default:
      return "The Lead reads it at its next checkpoint.";
  }
}

export interface JobNote {
  /** ISO instant, "" when the note carries none. */
  at: string;
  /** "operator", or the agent it came through. */
  by: string;
  text: string;
}

const NOTE = /^\[(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}) UTC, (?:(operator)|via ([^\]]+))\] ?([\s\S]*)$/;

/** A note as stored ("[2026-09-29 18:40 UTC, operator] …" or "… UTC, via assistant] …"): when, who, what. */
export function parseNote(raw: string): JobNote {
  const m = NOTE.exec(raw);
  if (!m) return { at: "", by: "", text: raw };
  return { at: `${m[1]}T${m[2]}:00Z`, by: m[3] ?? m[4] ?? "", text: m[5] };
}

// ─── refusals ───

const REFUSAL: Record<string, string> = {
  jobs_not_enabled: "Team tasks are switched off for this workspace (Settings → General).",
  no_time_limit: "Give a time limit: 10 minutes to 24 hours.",
  runtime_unsupported: "This workspace's gateway is too old to run Team tasks (OpenClaw 2026.9 or later).",
  not_awaiting_approval: "The plan is not waiting for approval any more.",
  not_live: "This Team task has ended.",
  not_found: "This Team task is not in this workspace.",
  no_reader: "No checkpoint is left and the handover has started: nobody would read the note.",
};

/**
 * A reason the plan check gave (a refused plan, a checkpoint change it did
 * not apply) as a sentence: "time: the steps…" → "The steps…", a plan file
 * that broke the form names the step by its place ("step 2, minutes: …").
 */
export function plainRefusal(reason: string): string {
  const text = localTimes(reason.trim());
  if (/^no plan was recorded\b/i.test(text)) return "The Lead finished without handing in a plan.";
  const form = /^(plan|decision)\.json:\s*/i.exec(text);
  if (form) {
    const rest = text
      .slice(form[0].length)
      .replace(/\b(steps|team|checks|checkpoints)\.(\d+)(?:\.(\w+))?:\s*/g, (_m, list: string, i: string, field?: string) => {
        const noun = list === "team" ? "team member" : list.replace(/s$/, "");
        return `${noun} ${Number(i) + 1}: ${field ? `${field} ` : ""}`;
      })
      .replace(/Input should be greater than or equal to (\d+)/g, "must be at least $1")
      .replace(/Input should be less than or equal to (\d+)/g, "must be at most $1")
      .replace(/String should have at most (\d+) characters?/g, "must be at most $1 characters")
      .replace(/String should have at least (\d+) characters?/g, "must be at least $1 characters")
      .replace(/Field required/g, "is missing");
    return `The ${form[1].toLowerCase()} was not in the required form: ${rest}`;
  }
  const rest = text.replace(/^time:\s*/i, "");
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}

/** A job route's refusal as a sentence (a missing team in the words Give a task uses: the notice that installs it stands beside it); anything else as the Workflows area says it. */
export function jobErrorText(error: unknown): string {
  const code = workflowErrorCode(error);
  if (code === "team_missing") return TEAM_MISSING_TEXT;
  if (code && REFUSAL[code]) return REFUSAL[code];
  return workflowError(error);
}
